<script setup lang="ts">
import { onMounted } from 'vue'
import { RouterView, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import StoreHeader from './components/StoreHeader.vue'
import StoreFooter from './components/StoreFooter.vue'
import ToastNotification from './components/ToastNotification.vue'
import WhatsAppButton from './components/WhatsAppButton.vue'
import { useSettingsStore } from './stores/settings'

const settingsStore = useSettingsStore()
const route = useRoute()
const { t } = useI18n()

onMounted(async () => {
  await settingsStore.fetchSettings()

  if (settingsStore.settings) {
    const s = settingsStore.settings
    // Update page title
    const storeName = s.storeName || s.webshop?.storeName || 'Click Electra'
    const titleKey = route.meta.title as string
    const translatedTitle = titleKey ? t(titleKey) : ''
    // Only update if not already set by component (optional check, but good practice)
    if (!document.title || document.title === storeName) {
       const pageTitle = translatedTitle ? `${translatedTitle} | ${storeName}` : storeName
       document.title = pageTitle
    } else if (translatedTitle && document.title.includes(titleKey)) {
        // Fix raw key if present
        document.title = document.title.replace(titleKey, translatedTitle)
    } else {
        // Just force update for now to solve reported issue reliably
        const pageTitle = translatedTitle ? `${translatedTitle} | ${storeName}` : storeName
        document.title = pageTitle
    }
    
    // Apply theme color
    const themeColor = s.webshop?.primaryColor || 'blue'
    applyTheme(themeColor)
  }
})

function applyTheme(colorName: string) {
  const themes: Record<string, Record<string, string>> = {
    blue: {
      50: '239 246 255',
      100: '219 234 254',
      200: '191 219 254',
      300: '147 197 253',
      400: '96 165 250',
      500: '59 130 246',
      600: '37 99 235',
      700: '29 78 216',
      800: '30 64 175',
      900: '30 58 138',
    },
    green: {
      50: '236 253 245',
      100: '209 250 229',
      200: '167 243 208',
      300: '110 231 183',
      400: '52 211 153',
      500: '16 185 129',
      600: '5 150 105',
      700: '4 120 87',
      800: '6 95 70',
      900: '6 78 59',
    },
    red: {
      50: '254 242 242',
      100: '254 226 226',
      200: '254 202 202',
      300: '252 165 165',
      400: '248 113 113',
      500: '239 68 68',
      600: '220 38 38',
      700: '185 28 28',
      800: '153 27 27',
      900: '127 29 29',
    },
    yellow: {
      50: '255 251 235',
      100: '254 243 199',
      200: '253 230 138',
      300: '252 211 77',
      400: '251 191 36',
      500: '245 158 11',
      600: '217 119 6',
      700: '180 83 9',
      800: '146 64 14',
      900: '120 53 15',
    },
    purple: {
      50: '250 245 255',
      100: '243 232 255',
      200: '233 213 255',
      300: '216 180 254',
      400: '192 132 252',
      500: '168 85 247',
      600: '147 51 234',
      700: '126 34 206',
      800: '107 33 168',
      900: '88 28 135',
    },
    orange: {
      50: '255 247 237',
      100: '255 237 213',
      200: '254 215 170',
      300: '253 186 116',
      400: '251 146 60',
      500: '249 115 22',
      600: '234 88 12',
      700: '194 65 12',
      800: '154 52 18',
      900: '124 45 18',
    },  
  }

  const palette = themes[colorName] || themes.blue
  const root = document.documentElement
  
  if (palette) {
    Object.entries(palette).forEach(([shade, rgb]) => {
      root.style.setProperty(`--color-primary-${shade}`, rgb)
    })
  }
}
</script>

<template>
  <div class="min-h-screen flex flex-col bg-gray-50">
    <ToastNotification />
    
    <!-- Loader -->
    <!-- Loader (Neutral Colors) -->
    <div v-if="settingsStore.isLoading && !settingsStore.settings" class="fixed inset-0 bg-white z-50 flex flex-col items-center justify-center">
      <div class="w-12 h-12 border-4 border-gray-200 border-t-gray-900 rounded-full animate-spin mb-4"></div>
      <div class="text-gray-500 font-medium animate-pulse">Loading Store...</div>
    </div>

    <!-- Maintenance Mode -->
    <div v-else-if="settingsStore.settings?.webshop?.enabled === false" class="flex-col flex min-h-screen items-center justify-center bg-gray-50 p-4">
       <div class="text-center max-w-lg bg-white p-12 rounded-3xl shadow-xl">
          <div class="w-20 h-20 bg-primary-50 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg class="w-10 h-10 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h1 class="text-3xl font-bold mb-4 text-gray-900">{{ settingsStore.settings?.webshop?.storeName || 'Store' }} is Closed</h1>
          <p class="text-gray-600 text-lg leading-relaxed">We are currently undergoing maintenance to improve your shopping experience. Please check back later.</p>
       </div>
    </div>

    <!-- App Content -->
    <div v-else class="flex-col flex min-h-screen">
      <StoreHeader />
      <main class="flex-1">
        <RouterView />
      </main>
      <StoreFooter />
    </div>
    
    <!-- WhatsApp Floating Button -->
    <WhatsAppButton />
  </div>
</template>
